import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { pipeConfigPropTypes } from 'common/propTypes';
import Legacy from './Legacy/index';
import CustomScript from './CustomScript/index';
import Snippet from './Snippet/index';
import { PipeDefinitionUtility } from 'v2/common/quantConfig/index';
import { DAY_CANDLE_START_TIME, DAY_CANDLE_END_TIME } from 'common/constants';

const propTypes = {
  pipeConfig: pipeConfigPropTypes.isRequired,
  onUpdatePipeConfig: PropTypes.func.isRequired,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  trigger: PropTypes.func.isRequired,
  validator: PropTypes.shape({}).isRequired,
  pipeIndex: PropTypes.number,
  setError: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  existingPipeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const defaultProps = {
  pipeIndex: undefined
};

const PipeInput = ({
  pipeConfig,
  onUpdatePipeConfig: propsOnUpdatePipeConfig,
  control,
  errors,
  trigger,
  validator,
  pipeIndex,
  setError,
  clearErrors,
  existingPipeNames
}) => {
  const { category, config, chartConfig, type: pipeType } = pipeConfig;
  const inputChartKeys = useMemo(
    () => (_.includes(_.keys(chartConfig), 'volumeInterval') ? ['volumeInterval'] : []),
    [chartConfig]
  );
  const inputConfigKeys = useMemo(() => _.keys(config), [config]);
  const inputKeys = useMemo(() => [
    ...inputChartKeys, ...inputConfigKeys
  ], [inputChartKeys, inputConfigKeys]);
  const isDayCandleEnabled = PipeDefinitionUtility.getIsDayCandleEnabled(pipeType)

  const buildPipeConfig = (patch) => {
    const nextConfig = { ...config, ...patch };
    if (!isDayCandleEnabled) return nextConfig;
    
    if (nextConfig.candleInterval === '1 days') {
      return {
        ...nextConfig,
        ...(!('startWindow' in nextConfig) && { startWindow: DAY_CANDLE_START_TIME }),
        ...(!('endWindow' in nextConfig) && { endWindow: DAY_CANDLE_END_TIME }),
      };
    }

    if (nextConfig.candleInterval !== '1 days') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { startWindow, endWindow, ...restConfig } = nextConfig;
      return restConfig;
    }
    return nextConfig;
  };

  const onUpdatePipeConfig = useCallback((keyOrPartialConfig, value) => {
    const updateConfig = (newConfig) => {
      propsOnUpdatePipeConfig({ config: buildPipeConfig(newConfig) });
    };

    if (_.isObject(keyOrPartialConfig)) {
      updateConfig(keyOrPartialConfig);
    } else if (_.includes(inputChartKeys, keyOrPartialConfig)) {
      propsOnUpdatePipeConfig({ chartConfig: { ...chartConfig, [keyOrPartialConfig]: value } });
    } else {
      updateConfig({ [keyOrPartialConfig]: value });
    }
  }, [propsOnUpdatePipeConfig, config, inputChartKeys, chartConfig]);

  switch (category) {
    case 'customScript':
      return (
        <CustomScript
          pipeConfig={pipeConfig}
          onUpdatePipeConfig={onUpdatePipeConfig}
          control={control}
          errors={errors}
          trigger={trigger}
          validator={validator}
        />
      );
    case 'snippet':
      return (
        <Snippet
          pipeConfig={pipeConfig}
          onUpdatePipeConfig={onUpdatePipeConfig}
          control={control}
          errors={errors}
          validator={validator}
        />
      );
    default:
      return (
        <Legacy
          pipeConfig={pipeConfig}
          onUpdatePipeConfig={onUpdatePipeConfig}
          control={control}
          errors={errors}
          inputKeys={inputKeys}
          trigger={trigger}
          setError={setError}
          clearErrors={clearErrors}
          existingPipeNames={existingPipeNames}
          pipeIndex={pipeIndex}
          validator={validator}
        />
      );
  }
};

PipeInput.propTypes = propTypes;
PipeInput.defaultProps = defaultProps;

export default PipeInput;
